import type { Selection } from '@atlas/types'
import { Chip } from '@atlas/ui-primitives'
import { X } from 'lucide-react'

// Re-exported, not redeclared: `Selection` is the shared axis→values contract in
// `@atlas/types`. A local `Record<string, string[]>` was structurally identical,
// so nothing failed to typecheck — it just gave one concept two homes, and the
// two could drift apart silently.
export type { Selection }

export interface ChipSetProps {
	/** axis code -> selected value codes. */
	tuple: Selection
	/** Human labels per axis code (falls back to the code). */
	axisLabels?: Record<string, string>
	/** Axes that should show an "all" chip when untouched (DEC-16). */
	allAxes?: string[]
	/** When provided, each value chip becomes removable. */
	onRemove?: (axis: string, value: string) => void
}

/** One tuple's worth of chips: its ordinal and the selection it asserts. */
export interface TupleSelection {
	tupleId: number
	tuple: Selection
}

export interface TupleChipSetProps extends Omit<ChipSetProps, 'tuple' | 'onRemove'> {
	/** The object's tuples, in the order they should render. */
	tuples: TupleSelection[]
	/** When provided, each value chip becomes removable, scoped to its tuple. */
	onRemove?: (tupleId: number, axis: string, value: string) => void
	/** When provided, each tuple gets a remove affordance of its own. */
	onRemoveTuple?: (tupleId: number) => void
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

/**
 * Renders a classification tuple as per-axis chips (design §10.3). Untouched axes
 * in `allAxes` show an "all" marker (DEC-16). Composes the Chip atom; presentational + storied.
 */
export function ChipSet({ tuple, axisLabels = {}, allAxes = [], onRemove }: ChipSetProps) {
	const axes = Array.from(new Set([...Object.keys(tuple), ...allAxes]))
	const label = (axis: string) => axisLabels[axis] ?? cap(axis)

	return (
		<div className="flex flex-wrap gap-1.5">
			{axes.flatMap((axis) => {
				const values = tuple[axis] ?? []
				if (values.length === 0 && allAxes.includes(axis)) {
					return [
						<Chip key={`${axis}:all`} variant="muted" size="sm">
							{label(axis)}: all
						</Chip>,
					]
				}
				return values.map((v) => (
					// `title` keeps the full text reachable when a long value code
					// truncates inside the pill. The remove button goes through Chip's own
					// `onRemove` prop so it renders outside the label's truncating span.
					<Chip
						key={`${axis}:${v}`}
						variant="muted"
						size="sm"
						title={`${label(axis)}: ${v}`}
						onRemove={onRemove ? () => onRemove(axis, v) : undefined}
						removeLabel={`Remove ${label(axis)} ${v}`}
					>
						{label(axis)}: {v}
					</Chip>
				))
			})}
		</div>
	)
}

/**
 * Explains the "Assertion" heading to a user who has never heard of a tuple.
 * Exported so any surface introducing the concept uses the same words.
 */
export const ASSERTION_HINT =
	'An assertion is one independent group of tags — what the classification model calls a tuple. ' +
	'Each is a separate fact about this object, so a rule or search only matches when a SINGLE ' +
	'assertion satisfies it. Tags in different assertions were never claimed to go together.'

/**
 * Renders an object's classification as one chip group PER TUPLE, rather than a
 * single flat row. "Assertion" is the user-facing name for a tuple (see
 * {@link ASSERTION_HINT}).
 *
 * A tuple is an independent assertion about the object — "in campaign c123" and
 * "in work order wo7" are two separate facts, and the same axis may legitimately
 * hold different values in different tuples. Flattening them into one row would
 * show a pairing nobody asserted, and would render `locale: ja-JP` next to
 * `locale: en-US` as if the object were both at once. The visual separation IS
 * the meaning here, not decoration.
 */
export function TupleChipSet({ tuples, axisLabels = {}, allAxes = [], onRemove, onRemoveTuple }: TupleChipSetProps) {
	return (
		<div className="space-y-3">
			{tuples.map(({ tupleId, tuple }, index) => (
				<div key={tupleId} className="space-y-1.5">
					<div className="flex items-center justify-between">
						{/* "Assertion" is the user-facing name for what the model calls a
						    TUPLE — one independent group of tags. The word is explained on
						    hover because the concept is not self-evident from a heading.

						    Ordinals are positional, not names: tuples carry no human-facing
						    label, so numbering by position avoids implying a stable identity
						    the model does not have. */}
						<p className="text-ds-2xs font-medium uppercase tracking-widest text-ds-text-subtle" title={ASSERTION_HINT}>
							Assertion {index + 1}
						</p>
						{onRemoveTuple ? (
							<button
								type="button"
								aria-label={`Remove assertion ${index + 1}`}
								onClick={() => onRemoveTuple(tupleId)}
								className="text-ds-text-subtle opacity-70 hover:opacity-100"
							>
								<X className="h-3.5 w-3.5" />
							</button>
						) : null}
					</div>
					<ChipSet
						tuple={tuple}
						axisLabels={axisLabels}
						allAxes={allAxes}
						onRemove={onRemove ? (axis, value) => onRemove(tupleId, axis, value) : undefined}
					/>
				</div>
			))}
		</div>
	)
}
